/**
 * Preference Step Modal
 *
 * Third step of onboarding - collects shopping preference (Men's/Women's/Both)
 * Figma: Property 1=Sign up 3
 * Step 3 of 4
 */
"use client";

import { useState } from "react";

import { motion } from "framer-motion";

import { colors, modalSpacing } from "@/styles/design-tokens";

import { BasicModalShell } from "./BasicModalShell";
import { ModalPrimaryButton } from "./ModalPrimaryButton";
import { ModalProgressBar } from "./ModalProgressBar";
import { PreviousSection } from "./PreviousSection";

const SPACING = {
  // Use a clamp so small screens don't waste vertical space
  topPadding: "clamp(56px, 10vh, 106px)", // From Figma: top position of content
  buttonContainerPadding: "28px", // Padding top of button container
  buttonGap: "28px", // Gap between preference buttons
  previousPaddingTop: "50px", // Space between divider and PREVIOUS button
  previousPaddingBottom: "16px", // Padding below PREVIOUS button
} as const;

type ShoppingPreference = "WOMEN" | "MEN" | "BOTH";

interface PreferenceStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (preference: ShoppingPreference) => void;
  onBack: () => void;
  initialPreference?: ShoppingPreference | null;
  currentStep?: number;
  totalSteps?: number;
}

export function PreferenceStepModal({
  isOpen,
  onClose,
  onNext,
  onBack,
  initialPreference = null,
  currentStep = 3,
  totalSteps = 4,
}: PreferenceStepModalProps) {
  const [selectedPreference, setSelectedPreference] = useState<ShoppingPreference | null>(
    initialPreference
  );

  const preferences: Array<{ value: ShoppingPreference; label: string }> = [
    { value: "WOMEN", label: "WOMEN'S" },
    { value: "MEN", label: "MEN'S" },
    { value: "BOTH", label: "NO PREFERENCE" },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPreference) {
      onNext(selectedPreference);
    }
  };

  const handlePreferenceClick = (preference: ShoppingPreference) => {
    setSelectedPreference(preference);
  };

  return (
    <BasicModalShell
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      panelClassName="sm:min-h-[520px]"
      topSlot={
        <ModalProgressBar
          currentStep={currentStep}
          totalSteps={totalSteps}
          animateFromPrevious={true}
        />
      }
    >
      <div
        className="mx-auto flex w-full flex-col px-4 sm:px-0"
        style={{
          maxWidth: modalSpacing.signInContentWidth,
          gap: modalSpacing.sectionGap,
          paddingTop: SPACING.topPadding,
          paddingBottom: modalSpacing.contentBottomPadding,
        }}
      >
        {/* Header with Title and Subtitle */}
        <div
          className="flex flex-col items-center text-center"
          style={{ gap: modalSpacing.headerGap }}
        >
          <h2 className="text-title text-ink">What department do you prefer to shop?</h2>
          <p className="text-caption text-ink-secondary">
            This helps us personalize your shopping experience.
          </p>
        </div>

        {/* Form Container with Top Padding */}
        <div
          className="flex flex-col"
          style={{
            paddingTop: SPACING.buttonContainerPadding,
          }}
        >
          <form
            onSubmit={handleSubmit}
            className="flex flex-col"
            style={{ gap: SPACING.buttonGap }}
          >
            {/* Preference Buttons */}
            <div className="flex flex-col" style={{ gap: SPACING.buttonGap }}>
              {preferences.map((preference, index) => (
                <motion.button
                  key={preference.value}
                  type="button"
                  onClick={() => handlePreferenceClick(preference.value)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex w-full items-center justify-center border transition-all duration-200 ${
                    selectedPreference === preference.value
                      ? "border-black bg-black"
                      : "bg-white hover:bg-gray-50"
                  }`}
                  style={{
                    height: modalSpacing.buttonHeight,
                    borderColor:
                      selectedPreference === preference.value
                        ? colors.stroke.pureBlack
                        : colors.accent.lightGray,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "10px",
                      fontWeight: 400,
                      lineHeight: "15px",
                      letterSpacing: "1.1172px",
                      textTransform: "uppercase" as const,
                      color:
                        selectedPreference === preference.value
                          ? colors.text.white
                          : colors.text.black,
                    }}
                  >
                    {preference.label}
                  </span>
                </motion.button>
              ))}
            </div>

            {/* Next Button */}
            <ModalPrimaryButton type="submit" disabled={!selectedPreference}>
              Next
            </ModalPrimaryButton>
          </form>

          {/* Previous Section */}
          <PreviousSection
            onBack={onBack}
            paddingTop={SPACING.previousPaddingTop}
            paddingBottom={SPACING.previousPaddingBottom}
          />
        </div>
      </div>
    </BasicModalShell>
  );
}
